import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from '@tanstack/react-router';
import { Ban, CircleCheck, Hourglass, TriangleAlert, type LucideIcon } from 'lucide-react';
import { z } from 'zod';
import { buttonVariants } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { formatCount, formatTaka } from '@/utils/number';
import {
  PCMP_CORRECTION_CATEGORY_CODE,
  PCMP_CORRECTION_COPY,
  PCMP_MKT_COMMENT_MAX,
} from '../../constants/pcmpProposal.constants';
import {
  describePcmpActOutcome,
  PCMP_REQUEST_DECISION_COPY,
} from '../../constants/pcmpRequested.constants';
import {
  useActOnPcmpProposalRequest,
  useActOnPcmpRequests,
} from '../../hooks/useActOnPcmpRequests';
import type { PcmpSkippedProposal } from '../../types/pcmpQueue.types';
import type {
  PcmpRequestActResult,
  PcmpRequestActSelection,
  PcmpRequestDecision,
} from '../../types/pcmpRequested.types';
import { extractSkippedProposals } from '../../utils/pcmpRequestErrors';
import { PcmpOutcomeDialog } from '../PcmpBatch/PcmpOutcomeDialog';
import { PcmpCommentField } from '../PcmpMarketingActions/PcmpCommentField';
import { PcmpEditDialog } from '../PcmpProposalEdit/PcmpEditDialog';

const DECISION_ICON = {
  PROCESS: Hourglass,
  APPROVE: CircleCheck,
  REJECT: Ban,
} as const satisfies Record<PcmpRequestDecision, LucideIcon>;

const commentSchema = (decision: PcmpRequestDecision) =>
  z.object({
    comment: z
      .string()
      .trim()
      .max(PCMP_MKT_COMMENT_MAX, `Comment must be ${PCMP_MKT_COMMENT_MAX} characters or fewer.`)
      .refine((value) => decision !== 'REJECT' || value.length > 0, 'A reason is required.'),
  });

type CommentValues = z.infer<ReturnType<typeof commentSchema>>;

export type PcmpRequestActRun =
  | { kind: 'bulk'; selection: PcmpRequestActSelection }
  | { kind: 'single'; proposalId: number };

export interface PcmpRequestActTarget {
  decision: PcmpRequestDecision;
  count: number;
  amount: number;
  run: PcmpRequestActRun;
  /** What the dialog is about, e.g. the ref no of one proposal. */
  subject?: string | undefined;
  /** One proposal: why approving would be refused now (the dialog then cannot approve). */
  blockedReason?: string | null | undefined;
  warning?: string | null | undefined;
  /** The category of the request(s): a Proposal Correction's decision settles the edits made under it. */
  categoryCode?: string | null | undefined;
}

interface PcmpRequestActDialogProps {
  target: PcmpRequestActTarget;
  refNoOf?: ((proposalId: number) => string | undefined) | undefined;
  /** Called once requests were acted on, so the caller can clear its selection. */
  onActed: () => void;
  onClose: () => void;
}

interface Outcome {
  title: string;
  summary: string;
  skipped: PcmpSkippedProposal[];
  result: PcmpRequestActResult | null;
}

/**
 * Legacy RequestAct: process, approve or reject one request or many. Approving
 * runs the category's action; a refused one is left out with its reason and
 * its request stays open. A reject needs a reason, the others a note at most.
 */
export function PcmpRequestActDialog({
  target,
  refNoOf,
  onActed,
  onClose,
}: PcmpRequestActDialogProps) {
  const { decision, run } = target;
  const copy = PCMP_REQUEST_DECISION_COPY[decision];
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const form = useForm<CommentValues>({
    resolver: zodResolver(commentSchema(decision)),
    defaultValues: { comment: '' },
  });

  const bulk = useActOnPcmpRequests((result) => {
    onActed();
    if (result.skipped.length > 0) {
      setOutcome({
        title: describePcmpActOutcome(result),
        summary: 'The requests below were left out and stay open; the reason is listed.',
        skipped: result.skipped,
        result,
      });
    } else {
      onClose();
    }
  });
  const single = useActOnPcmpProposalRequest(
    run.kind === 'single' ? run.proposalId : 0,
    decision,
    () => {
      onActed();
      onClose();
    },
  );

  const active = run.kind === 'bulk' ? bulk : single;
  // The API refuses the whole call (409) when none could be acted on and says why per proposal.
  const refusedSkips = run.kind === 'bulk' ? extractSkippedProposals(bulk.error) : null;
  const isBlocked = decision === 'APPROVE' && Boolean(target.blockedReason);
  const isCorrection = target.categoryCode === PCMP_CORRECTION_CATEGORY_CODE;
  const correctionCopy =
    isCorrection && decision !== 'PROCESS'
      ? PCMP_CORRECTION_COPY[decision === 'APPROVE' ? 'approve' : 'reject']
      : null;

  const onSubmit = form.handleSubmit((values) => {
    const comment = values.comment === '' ? undefined : values.comment;
    if (run.kind === 'bulk') {
      bulk.mutate({ decision, comment, ...run.selection });
    } else {
      single.mutate(comment);
    }
  });

  if (outcome) {
    const batches = outcome.result?.urgentBatches ?? [];
    return (
      <PcmpOutcomeDialog
        title={outcome.title}
        summary={outcome.summary}
        skipped={outcome.skipped}
        refNoOf={refNoOf}
        actions={
          batches.length > 0 ? (
            <>
              {batches.map((batch) => (
                <Link
                  key={batch.batchNo}
                  to="/pcmp/requests/batches/$batchNo"
                  params={{ batchNo: batch.batchNo }}
                  className={buttonVariants({ variant: 'outline' })}
                >
                  Open urgent batch #{batch.batchNo}
                </Link>
              ))}
            </>
          ) : undefined
        }
        onClose={onClose}
      />
    );
  }

  if (refusedSkips) {
    return (
      <PcmpOutcomeDialog
        title="Nothing could be done"
        summary="None of the selected requests could be acted on. Each stays open; the reason is listed."
        skipped={refusedSkips}
        refNoOf={refNoOf}
        onClose={() => {
          bulk.reset();
          onClose();
        }}
      />
    );
  }

  return (
    <Form {...form}>
      <PcmpEditDialog
        open
        onOpenChange={(open) => !open && onClose()}
        title={copy.title}
        {...(target.subject ? { description: target.subject } : {})}
        icon={DECISION_ICON[decision]}
        isDirty={form.formState.isDirty}
        isPending={active.isPending}
        error={active.error}
        onSubmit={(event) => void onSubmit(event)}
        submitLabel={copy.submit}
        submitVariant={decision === 'REJECT' ? 'destructive' : 'default'}
        pendingLabel={copy.pending}
        {...(isBlocked ? { submitDisabled: true } : {})}
      >
        <div className="flex flex-col gap-1 rounded-lg border bg-muted/40 px-4 py-3 text-sm">
          <span className="text-xs text-muted-foreground">Requests</span>
          <span className="font-medium">
            {formatCount(target.count, 'request')} · {formatTaka(target.amount)}
          </span>
        </div>
        {isBlocked ? (
          <div
            role="alert"
            className="flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
          >
            <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
            <span>Cannot be approved now: {target.blockedReason}</span>
          </div>
        ) : null}
        {correctionCopy ? (
          <div className="flex items-start gap-3 rounded-lg border border-warning-foreground/30 bg-warning px-4 py-3 text-sm text-warning-foreground">
            <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
            <span>{correctionCopy}</span>
          </div>
        ) : null}
        {decision === 'APPROVE' && target.warning ? (
          <div className="flex items-start gap-3 rounded-lg border border-warning-foreground/30 bg-warning px-4 py-3 text-sm text-warning-foreground">
            <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
            <span>{target.warning}</span>
          </div>
        ) : null}
        <p className="text-sm text-muted-foreground">{copy.note}</p>
        <PcmpCommentField
          control={form.control}
          name="comment"
          label={decision === 'REJECT' ? 'Reason' : 'Comment'}
          required={decision === 'REJECT'}
          placeholder={copy.placeholder}
          description="Recorded on the proposal's timeline."
        />
      </PcmpEditDialog>
    </Form>
  );
}
